import React, { useEffect, useMemo, useState } from 'react';

const getInviteStatusLabel = (status) => {
  if (status === 'accepted') return 'Accepted';
  if (status === 'revoked') return 'Revoked';
  return 'Pending';
};

const getWorkspaceLabel = (workspace, currentUserId, options = {}) => {
  if (!workspace) return 'Shared workspace';
  if (workspace.ownerId && workspace.ownerId === currentUserId) {
    return options.ownerLabel || 'My shared ledger';
  }
  return workspace.ownerDisplayName || workspace.ownerEmail || 'Shared workspace';
};

function AccountantAccessModal({
  isOpen,
  onClose,
  user,
  activeWorkspace,
  ownWorkspace,
  acceptedWorkspaces = [],
  pendingInvites = [],
  sentInvites = [],
  onSendInvite,
  onAcceptInvite,
  onRevokeInvite,
  onSelectWorkspace,
  onRecoverLocalTransactions,
  onReplaceSharedLedgerFromLocal,
  canRecoverLocalTransactions = false,
  inviteDisabledReason = '',
  isBusy = false,
  isRefreshing = false
}) {
  const [accountantEmail, setAccountantEmail] = useState('');
  const [localError, setLocalError] = useState('');
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAccountantEmail('');
      setLocalError('');
      setShowTroubleshooting(false);
    }
  }, [isOpen]);

  const canUsePersonalData = !ownWorkspace;
  const hasSharedOptions = Boolean(ownWorkspace) || acceptedWorkspaces.length > 0;
  const selectedWorkspaceId = activeWorkspace?.id || '';
  const personalSelected = !activeWorkspace && canUsePersonalData;

  const sentInviteRows = useMemo(
    () => sentInvites.filter(Boolean),
    [sentInvites]
  );
  const hasLiveSentInvite = sentInviteRows.some((invite) =>
    invite?.status === 'pending' || invite?.status === 'accepted'
  );
  const ownerWorkspaceLabel = hasLiveSentInvite ? 'My shared ledger' : 'My ledger';

  if (!isOpen) return null;

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setLocalError('');

    try {
      await onSendInvite?.(accountantEmail);
      setAccountantEmail('');
    } catch (error) {
      setLocalError(error?.message || 'Unable to send invitation.');
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    setLocalError('');
    try {
      await onAcceptInvite?.(inviteId);
    } catch (error) {
      setLocalError(error?.message || 'Unable to accept invitation.');
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    setLocalError('');
    try {
      await onRevokeInvite?.(inviteId);
    } catch (error) {
      setLocalError(error?.message || 'Unable to update invitation.');
    }
  };

  const handleRecoverLocalTransactions = async () => {
    setLocalError('');
    try {
      await onRecoverLocalTransactions?.();
    } catch (error) {
      setLocalError(error?.message || 'Unable to recover local transactions.');
    }
  };

  const handleReplaceSharedLedgerFromLocal = async () => {
    setLocalError('');
    try {
      await onReplaceSharedLedgerFromLocal?.();
    } catch (error) {
      setLocalError(error?.message || 'Unable to replace shared ledger from local transactions.');
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Accountant Access</h2>
            <p className="mt-1 text-sm text-gray-600">
              Invite an accountant by email so they can open the same shared ledger and edit the same dashboard data.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-5">
          <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Shared access covers transactions, pricing, clients, goals, categories, cost items, depreciation, and VAT settings.
            Receipt images remain on the owner account in this version.
          </div>

          {(localError || isRefreshing) && (
            <div className={`mb-5 rounded-xl px-4 py-3 text-sm ${localError ? 'border border-red-200 bg-red-50 text-red-700' : 'border border-blue-200 bg-blue-50 text-blue-700'}`}>
              {localError || 'Refreshing collaboration access...'}
            </div>
          )}

          <div className="grid gap-5 lg:grid-cols-2">
            <section className="rounded-2xl border border-gray-200 p-4">
              <h3 className="text-base font-semibold text-gray-900">Invite Accountant</h3>
              <p className="mt-1 text-sm text-gray-600">
                The invite appears when the accountant signs in with the email you enter here.
              </p>

              <form className="mt-4 space-y-3" onSubmit={handleInviteSubmit}>
                <input
                  type="email"
                  value={accountantEmail}
                  onChange={(event) => setAccountantEmail(event.target.value)}
                  placeholder="accountant@example.com"
                  disabled={Boolean(inviteDisabledReason) || isBusy}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                <button
                  type="submit"
                  disabled={Boolean(inviteDisabledReason) || isBusy || !accountantEmail.trim()}
                  className="w-full rounded-xl bg-[color:var(--color-info)] px-4 py-3 text-sm font-semibold text-[color:var(--color-text)] transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Send Invitation
                </button>
              </form>

              {inviteDisabledReason && (
                <p className="mt-3 text-xs text-amber-700">{inviteDisabledReason}</p>
              )}

              <div className="mt-5 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900">Sent Invites</h4>
                {sentInviteRows.length === 0 ? (
                  <p className="text-sm text-gray-500">No invitations sent yet.</p>
                ) : (
                  sentInviteRows.map((invite) => (
                    <div key={invite.id} className="rounded-xl border border-gray-200 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{invite.inviteeEmail}</div>
                          <div className="mt-1 text-xs text-gray-500">
                            {getInviteStatusLabel(invite.status)}
                            {invite.acceptedByEmail ? ` by ${invite.acceptedByEmail}` : ''}
                          </div>
                        </div>
                        {invite.status !== 'revoked' && (
                          <button
                            type="button"
                            onClick={() => handleRevokeInvite(invite.id)}
                            disabled={isBusy}
                            className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 p-4">
              <h3 className="text-base font-semibold text-gray-900">Incoming Invites</h3>
              <p className="mt-1 text-sm text-gray-600">
                Accept an invite to open a client ledger inside your account.
              </p>

              <div className="mt-4 space-y-3">
                {pendingInvites.length === 0 ? (
                  <p className="text-sm text-gray-500">No pending invitations for {user?.email || 'this account'}.</p>
                ) : (
                  pendingInvites.map((invite) => (
                    <div key={invite.id} className="rounded-xl border border-gray-200 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invite.ownerDisplayName || invite.ownerEmail || 'Workspace owner'}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {invite.ownerEmail || 'Owner email unavailable'}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAcceptInvite(invite.id)}
                          disabled={isBusy}
                          className="rounded-lg bg-[color:var(--color-accent)] px-3 py-2 text-xs font-semibold text-[color:var(--color-text)] transition hover:brightness-95 disabled:opacity-60"
                        >
                          Accept
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-5 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900">Workspace Selection</h4>
                {!hasSharedOptions && canUsePersonalData ? (
                  <p className="text-sm text-gray-500">You are currently using your own personal ledger.</p>
                ) : (
                  <div className="space-y-2">
                    {canUsePersonalData && (
                      <button
                        type="button"
                        onClick={() => onSelectWorkspace?.(null)}
                        disabled={isBusy}
                        className={`w-full rounded-xl border px-4 py-3 text-left text-sm transition ${
                          personalSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Use my own data
                      </button>
                    )}

                    {ownWorkspace && (
                      <button
                        type="button"
                        onClick={() => onSelectWorkspace?.(ownWorkspace.id)}
                        disabled={isBusy}
                        className={`w-full rounded-xl border px-4 py-3 text-left text-sm transition ${
                          selectedWorkspaceId === ownWorkspace.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {getWorkspaceLabel(ownWorkspace, user?.uid, { ownerLabel: ownerWorkspaceLabel })}
                      </button>
                    )}

                    {acceptedWorkspaces.map((workspace) => (
                      <button
                        key={workspace.id}
                        type="button"
                        onClick={() => onSelectWorkspace?.(workspace.id)}
                        disabled={isBusy}
                        className={`w-full rounded-xl border px-4 py-3 text-left text-sm transition ${
                          selectedWorkspaceId === workspace.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {getWorkspaceLabel(workspace, user?.uid)}
                      </button>
                    ))}
                  </div>
                )}

                <div className="rounded-xl bg-gray-50 px-4 py-3 text-xs text-gray-600">
                  Active workspace:{' '}
                  <span className="font-medium text-gray-800">
                    {personalSelected
                      ? 'My own data'
                      : getWorkspaceLabel(activeWorkspace, user?.uid, { ownerLabel: ownerWorkspaceLabel })}
                  </span>
                </div>

                {canRecoverLocalTransactions && ownWorkspace && (
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setShowTroubleshooting((prev) => !prev)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                      aria-expanded={showTroubleshooting}
                    >
                      Troubleshooting
                    </button>

                    {showTroubleshooting && (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                        <div className="text-sm font-semibold text-amber-900">Recover local transactions</div>
                        <p className="mt-1 text-xs text-amber-800">
                          Use this only when advised to fix missing shared-workspace data.
                        </p>
                        <button
                          type="button"
                          onClick={handleRecoverLocalTransactions}
                          disabled={isBusy}
                          className="mt-3 w-full rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Recover into shared ledger
                        </button>
                        <p className="mt-3 text-xs text-red-800">
                          Replaces this shared ledger with this owner account's local or Google Drive records.
                        </p>
                        <button
                          type="button"
                          onClick={handleReplaceSharedLedgerFromLocal}
                          disabled={isBusy}
                          className="mt-2 w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Replace shared ledger from local records
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountantAccessModal;
